import axios from "axios";
import { trustedDeviceStorageKey, authStorageKey, productionHades, credentialEvent } from "../config.js";
import { emptyCredential } from "../helpers/credentials.js";
import { getAccessTokenFromHeaders, getRefreshTokenFromHeaders, getIsTrustedDeviceFromHeaders, getMissingConsentsFromHeaders } from "../helpers/headers.js";
import { getItem } from "../hooks/useStickyState.js";
import "../helpers/time.js";
import "react";
const defaultHadesSettings = {
  serverUrl: productionHades
};
const hades = typeof window !== "undefined" ? window.hades || defaultHadesSettings : defaultHadesSettings;
const http = axios.create({
  baseURL: hades.serverUrl,
  withCredentials: true
});
const loginWithGoogle = (redirectUri = window.location.origin) => {
  const encodedUri = decodeURIComponent(redirectUri);
  window.location.href = `${window.hades.serverUrl}/api/authenticate?redirect_uri=${encodedUri}`;
};
const loginWithEmailAndPassword = async (credentials, endpoint, additionalHeaders) => {
  const trustedDeviceRaw = getItem(trustedDeviceStorageKey);
  const trustedDeviceId = trustedDeviceRaw ? JSON.parse(trustedDeviceRaw) : void 0;
  const requestBody = {
    ...credentials,
    ...trustedDeviceId && { trustedDeviceId }
  };
  const isFormUrlEncoded = (additionalHeaders == null ? void 0 : additionalHeaders["Content-Type"]) === "application/x-www-form-urlencoded";
  const body = isFormUrlEncoded ? new URLSearchParams(requestBody) : requestBody;
  const { data, headers } = await http.post(endpoint || "/api/authenticate", body, {
    ...additionalHeaders && { headers: additionalHeaders }
  });
  return {
    token: getAccessTokenFromHeaders(headers) ?? "",
    refreshToken: getRefreshTokenFromHeaders(headers),
    isTrustedDevice: getIsTrustedDeviceFromHeaders(headers) ?? false,
    user: data
  };
};
const logout = async ({ token, kind }) => await http.post(`/api/authenticate/logout?logoutReason=${kind}`, {}, { headers: { Authorization: token } });
const authenticateComplete = async (sessionId) => {
  try {
    const { data: user, headers } = await http.get("/api/authenticate/complete", {
      headers: {
        SESSION: sessionId
      }
    });
    return {
      token: getAccessTokenFromHeaders(headers),
      refreshToken: getRefreshTokenFromHeaders(headers),
      user
    };
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      return Promise.reject(e.response.data);
    }
    return Promise.reject(e);
  }
};
const postEnrollments = async (fields) => {
  const data = await http.post(`/api/users/patient/v2/register/short/enrollments`, fields);
  return data.data;
};
const getCurrentUser = async (token) => {
  try {
    const { data } = await http.get("/api/users/current", {
      headers: {
        Authorization: token
      }
    });
    return data;
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      return Promise.reject(e.response.data);
    }
    return Promise.reject(e);
  }
};
const clearAuth = () => {
  localStorage.setItem(authStorageKey, JSON.stringify(emptyCredential));
};
const getToken = async (refreshToken) => {
  const { data: user, headers } = await http.post("/api/authenticate/token", {
    token: refreshToken
  });
  return {
    token: getAccessTokenFromHeaders(headers),
    refreshToken: getRefreshTokenFromHeaders(headers),
    user
  };
};
const getRefreshedCredential = ({ token, user }) => {
  const currentCredentials = localStorage.getItem(authStorageKey);
  try {
    const parsedCredentials = JSON.parse(currentCredentials ?? "");
    return {
      ...parsedCredentials,
      user,
      token
    };
  } catch (e) {
    return { user, token };
  }
};
const persistCredential = (credential) => {
  localStorage.setItem(authStorageKey, JSON.stringify(credential));
  return credential;
};
const dispatchCredential = (credential) => {
  window.dispatchEvent(new CustomEvent(credentialEvent, { detail: credential }));
  return credential;
};
const getCredentials = () => {
  const credentials = getItem(authStorageKey);
  try {
    if (credentials) {
      const parsedCredentials = JSON.parse(credentials);
      if (parsedCredentials)
        return parsedCredentials;
    } else {
      return {
        token: null,
        refreshToken: null,
        authentication: null,
        missingConsents: null
      };
    }
  } catch (e) {
    return null;
  }
};
const loginPath = (win) => {
  var _a, _b, _c, _d;
  const isHashHistory = ((_a = win == null ? void 0 : win.location) == null ? void 0 : _a.hash) !== "";
  return isHashHistory && ((_b = win == null ? void 0 : win.hades) == null ? void 0 : _b.loginPath) ? `#${((_c = win == null ? void 0 : win.hades) == null ? void 0 : _c.loginPath) ?? "/login"}` : ((_d = win == null ? void 0 : win.hades) == null ? void 0 : _d.loginPath) ?? "/login";
};
const refreshCredential = (refreshToken) => getToken(refreshToken).then(getRefreshedCredential).then(persistCredential).then(dispatchCredential);
const requestInterceptor = async (config) => {
  const controller = new AbortController();
  const abortAndRedirect = () => {
    controller.abort();
    if (window) {
      window.location.href = `${window.location.origin}${loginPath(window)}`;
    }
  };
  try {
    const auth = getItem(authStorageKey);
    if (auth) {
      const { token } = JSON.parse(auth);
      if (token && config.headers) {
        config.headers["authorization"] = token;
      } else {
        abortAndRedirect();
      }
    } else {
      abortAndRedirect();
    }
  } catch (e) {
  }
  return {
    ...config,
    signal: controller.signal
  };
};
const responseErrorInterceptor = async (error) => {
  var _a, _b;
  if (((_a = error == null ? void 0 : error.response) == null ? void 0 : _a.status) === 401) {
    const config = error.config;
    try {
      const auth = getItem(authStorageKey);
      if (auth) {
        const { refreshToken } = JSON.parse(auth);
        if (refreshToken) {
          try {
            const { token: newToken } = await refreshCredential(refreshToken);
            if (config.headers && newToken) {
              config.headers["authorization"] = newToken;
            }
          } catch {
            clearAuth();
            if (window)
              window.location.href = `${window.location.origin}${loginPath(window)}?error=session_expired`;
          }
          return axios(config);
        }
      }
    } catch (e) {
    }
  }
  if (window && ((_b = error == null ? void 0 : error.response) == null ? void 0 : _b.status) === 401) {
    window.location.href = `${window.location.origin}${loginPath(window)}`;
  }
  return Promise.reject(error);
};
const hadesRequestInterceptor = (config) => {
  config.baseURL = window.hades.serverUrl;
  if (window.hades.u_s) {
    config.headers = { ...config.headers, u_s: window.hades.u_s };
  }
  return config;
};
http.interceptors.request.use(hadesRequestInterceptor);
const addAuthInterceptors = (axiosInstance) => {
  axiosInstance.interceptors.request.use(requestInterceptor);
  axiosInstance.interceptors.response.use((config) => config, responseErrorInterceptor);
};
const twoFactorVerification = async (body) => {
  try {
    const credentials = getCredentials();
    const { data, headers } = await http.post("/api/authenticate/verify", body, {
      headers: {
        Authorization: credentials == null ? void 0 : credentials.token
      }
    });
    return {
      token: getAccessTokenFromHeaders(headers),
      refreshToken: getRefreshTokenFromHeaders(headers),
      missingConsents: getMissingConsentsFromHeaders(headers),
      trustedDeviceId: data.trustedDeviceId,
      user: data
    };
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      return Promise.reject(e.response.data);
    }
    return Promise.reject(e);
  }
};
const postRegisterUser = async (body) => {
  try {
    const { headers } = await http.post("/api/users/patient/v2/register", body);
    return {
      token: getAccessTokenFromHeaders(headers) ?? ""
    };
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      return Promise.reject({
        ...e.response.data,
        payload: JSON.parse(e.config.data)
      });
    }
    return Promise.reject(e);
  }
};
const postShortRegisterUser = async ({
  token,
  ...body
}) => {
  try {
    const { headers } = await http.post(`/api/users/patient/v2/register/short/${token}`, body);
    return {
      token: getAccessTokenFromHeaders(headers) ?? ""
    };
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      return Promise.reject({
        ...e.response.data,
        payload: JSON.parse(e.config.data)
      });
    }
    return Promise.reject(e);
  }
};
const patchSetPreferredLanguage = async ({ languageCode, referenceId }) => {
  const { token } = getCredentials();
  try {
    const { data } = await http.patch(
      `/api/users/patient/${referenceId}/language`,
      { languageCode },
      {
        headers: {
          Authorization: token
        }
      }
    );
    return data;
  } catch (e) {
    const errorMessage = axios.isAxiosError(e) && e.response ? e.response.data : "generic_error";
    return Promise.reject(errorMessage);
  }
};
const postVerifyPhone = async (body) => {
  const { token } = getCredentials();
  try {
    const { data, headers } = await http.post("/api/users/patient/v2/register/phone", body, {
      headers: {
        Authorization: token
      }
    });
    return {
      refreshToken: getRefreshTokenFromHeaders(headers),
      token: getAccessTokenFromHeaders(headers),
      trustedDeviceId: data.trustedDeviceId,
      user: data
    };
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      return Promise.reject(e.response.data);
    }
    return Promise.reject(e);
  }
};
const postVerifyPhoneReset = async (body) => {
  const { token: patientUsernameAndPasswordToken } = getCredentials();
  const { token, ...restOfBody } = body;
  try {
    const { data, headers } = await http.post(
      "/api/users/patient/reset-phone-number/verify-phone",
      {
        ...restOfBody,
        patientUsernameAndPasswordToken
      },
      {
        headers: {
          Authorization: token
        }
      }
    );
    return {
      refreshToken: getRefreshTokenFromHeaders(headers),
      token: getAccessTokenFromHeaders(headers),
      user: data
    };
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      return Promise.reject(e.response.data);
    }
    return Promise.reject(e);
  }
};
const postVerifyEmail = async (token, mobileToken) => {
  const { data, headers } = await http.post(
    `/api/users/patient/v2/register/${token}`,
    mobileToken ? {
      mobileToken
    } : void 0
  );
  return {
    user: data,
    token: getAccessTokenFromHeaders(headers),
    refreshToken: getRefreshTokenFromHeaders(headers)
  };
};
const postResetPasswordInitialization = async (body) => {
  try {
    const { data } = await http.post("/api/users/patient/reset-password", body);
    return data;
  } catch (e) {
    const errorMessage = axios.isAxiosError(e) && e.response ? e.response.data : "generic_error";
    return Promise.reject(errorMessage);
  }
};
const postResetPhoneNumberInitialization = async (body) => {
  const { token } = getCredentials();
  try {
    const { data } = await http.post("/api/users/patient/reset-phone-number", body, {
      headers: { Authorization: token }
    });
    return data;
  } catch (e) {
    const errorMessage = axios.isAxiosError(e) && e.response ? e.response.data : "generic_error";
    return Promise.reject(errorMessage);
  }
};
const postResendVerificationCode = async () => {
  const { token } = getCredentials();
  try {
    const {
      headers: { authorization }
    } = await http.post("/api/users/patient/v2/register/resend-verification-code", void 0, {
      headers: { Authorization: token }
    });
    return authorization;
  } catch (e) {
    const errorMessage = axios.isAxiosError(e) && e.response ? e.response.data : "generic_error";
    return Promise.reject(errorMessage);
  }
};
const postResendVerificationEmail = async () => {
  const { token } = getCredentials();
  try {
    const { data } = await http.post("/api/users/patient/v2/register/resend-verification-email", void 0, {
      headers: { Authorization: token }
    });
    return data;
  } catch (e) {
    const errorMessage = axios.isAxiosError(e) && e.response ? e.response.data : "generic_error";
    return Promise.reject(errorMessage);
  }
};
const getCheckResetPasswordToken = async (body) => {
  try {
    const { data } = await http.get(`/api/users/patient/reset-password/${body.token}`);
    return data;
  } catch (e) {
    const errorMessage = axios.isAxiosError(e) && e.response ? e.response.data : "generic_error";
    return Promise.reject(errorMessage);
  }
};
const getCheckResetPhoneNumberToken = async (body) => {
  try {
    const { data } = await http.get(`/api/users/patient/reset-phone-number/${body == null ? void 0 : body.token}/validate`);
    return data;
  } catch (e) {
    const errorMessage = axios.isAxiosError(e) && e.response ? e.response.data : "generic_error";
    return Promise.reject(errorMessage);
  }
};
const putResetPassword = async (body) => {
  try {
    const { data } = await http.put(
      "/api/users/patient/reset-password",
      { newPassword: body == null ? void 0 : body.newPassword },
      {
        headers: {
          Authorization: body.token
        }
      }
    );
    return data;
  } catch (e) {
    const errorMessage = axios.isAxiosError(e) && e.response ? e.response.data : "generic_error";
    return Promise.reject(errorMessage);
  }
};
const postResetPhoneNumber = async (body) => {
  try {
    const { headers } = await http.post(
      `/api/users/patient/reset-phone-number/submit`,
      { newPhoneNumber: body == null ? void 0 : body.newPhoneNumber },
      {
        headers: {
          Authorization: body.token
        }
      }
    );
    return {
      token: getAccessTokenFromHeaders(headers) ?? ""
    };
  } catch (e) {
    const errorMessage = axios.isAxiosError(e) && e.response ? e.response.data : "generic_error";
    return Promise.reject(errorMessage);
  }
};
const postMissingConsents = async (body) => {
  const { token, authentication } = getCredentials();
  const personReferenceId = authentication == null ? void 0 : authentication.personReferenceId;
  try {
    const { data, headers } = await http.post(
      "/api/users/patient/consents",
      { personReferenceId, ...body },
      {
        headers: {
          Authorization: token
        }
      }
    );
    return {
      token: getAccessTokenFromHeaders(headers),
      refreshToken: getRefreshTokenFromHeaders(headers),
      missingConsents: getMissingConsentsFromHeaders(headers),
      user: data
    };
  } catch (e) {
    const errorMessage = axios.isAxiosError(e) && e.response ? e.response.data : "generic_error";
    return Promise.reject(errorMessage);
  }
};
const registerNewUserAccount = async (body) => {
  try {
    const { data } = await http.post("/api/public/v1/users", body);
    return data;
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      return Promise.reject(e.response.data);
    }
    return Promise.reject(e);
  }
};
const verifyNewUserAccount = async (token) => {
  try {
    const { data, headers } = await http.post(`/api/public/v1/users/emails/${token}`);
    return {
      token: getAccessTokenFromHeaders(headers) ?? "",
      refreshToken: getRefreshTokenFromHeaders(headers),
      user: data.user
    };
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      return Promise.reject(e.response.data);
    }
    return Promise.reject(e);
  }
};
const sendResetPasswordEmail = async (body) => {
  try {
    const { data } = await http.post("/api/public/v1/users/reset-password", body);
    return data;
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      return Promise.reject(e.response.data);
    }
    return Promise.reject(e);
  }
};
const getResetEmailToken = async (token) => {
  try {
    const { data } = await http.get(`/api/public/v1/users/reset-password/${token}`);
    return data;
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      return Promise.reject(e.response.data);
    }
    return Promise.reject(e);
  }
};
const putResetNewPassword = async (body) => {
  try {
    const { data } = await http.put(
      "/api/public/v1/users/reset-password",
      { newPassword: body == null ? void 0 : body.newPassword },
      {
        headers: {
          Authorization: body.token
        }
      }
    );
    return data;
  } catch (e) {
    const errorMessage = axios.isAxiosError(e) && e.response ? e.response.data : "generic_error";
    return Promise.reject(errorMessage);
  }
};
export {
  addAuthInterceptors,
  authenticateComplete,
  clearAuth,
  getCheckResetPasswordToken,
  getCheckResetPhoneNumberToken,
  getCurrentUser,
  getResetEmailToken,
  loginWithEmailAndPassword,
  loginWithGoogle,
  logout,
  patchSetPreferredLanguage,
  postEnrollments,
  postMissingConsents,
  postRegisterUser,
  postResendVerificationCode,
  postResendVerificationEmail,
  postResetPasswordInitialization,
  postResetPhoneNumber,
  postResetPhoneNumberInitialization,
  postShortRegisterUser,
  postVerifyEmail,
  postVerifyPhone,
  postVerifyPhoneReset,
  putResetNewPassword,
  putResetPassword,
  refreshCredential,
  registerNewUserAccount,
  sendResetPasswordEmail,
  twoFactorVerification,
  verifyNewUserAccount
};
